import { GridColumn } from '@entity-framework/entity-grid/state/entity-grid-column';
import { columnFormatters } from '@utils/display-formatters';
import { CellClassParams, CellStyleFunc, IAggFuncParams, ValueFormatterParams } from '@ag-grid-community/core';
import { ContributionRecordError } from './state/pension-contribution-employee.model';
import { StatusBadgeCellRendererComponent } from '@app/advanced-reports/ag-grid-custom-renderers/status-badge-cell-renderer.component';
import { hasErrors, getRowErrorClassRules, formatErrorDescriptions } from './grid-utils';

const errorCountAggFunc = (params: IAggFuncParams): number => {
  let totalErrors = 0;
  params.values.forEach((value) => {
    if (Array.isArray(value)) {
      totalErrors += value.length;
    } else if (typeof value === 'number') {
      totalErrors += value;
    }
  });
  return totalErrors;
};

const groupRowDash = (params: ValueFormatterParams): string => {
  if (params.node?.group) return '-';
  return params.value ?? '';
};

export const pensionContributionsGridColumns = (enableRowGroup = false, showEmployerRefPaymentSourceColumn = true): GridColumn[] => {
  const employerRefPaymentSourceColumn: GridColumn = {
    propertyName: 'employerRefPaymentSource',
    headerLabel: 'Employer ref. / Payment source',
    propertyType: 'text',
    sortable: true,
    formatForDisplay: (value: string) => value || '-',
    colDefOverrides: {
      flex: 1.5,
      minWidth: 180,
      cellClassRules: getRowErrorClassRules(),
      suppressColumnsToolPanel: true,
      hide: true,
      ...(enableRowGroup && { rowGroup: true })
    }
  };

  const statusColumn: GridColumn = {
    propertyName: 'submissionStatus',
    headerLabel: 'Status',
    propertyType: 'text',
    sortable: true,
    colDefOverrides: {
      flex: 1.2,
      minWidth: 150,
      cellClassRules: getRowErrorClassRules(),
      cellRenderer: StatusBadgeCellRendererComponent,
      aggFunc: 'first',
      valueFormatter: (params: ValueFormatterParams) => (params.node?.group ? (params.value ?? '') : '')
    }
  };

  const restColumns: GridColumn[] = [
    ...(enableRowGroup ? [statusColumn] : []),
    {
      propertyName: 'employmentId',
      headerLabel: 'Employment id',
      propertyType: 'text',
      sortable: true,
      colDefOverrides: {
        flex: 1,
        minWidth: 120,
        cellClassRules: getRowErrorClassRules(),
        ...(enableRowGroup && { valueFormatter: groupRowDash })
      }
    },
    {
      propertyName: 'surname',
      headerLabel: 'Surname',
      propertyType: 'text',
      sortable: true,
      colDefOverrides: {
        flex: 1,
        minWidth: 120,
        cellClassRules: getRowErrorClassRules(),
        ...(enableRowGroup && { valueFormatter: groupRowDash })
      }
    },
    {
      propertyName: 'firstName',
      headerLabel: 'First name',
      propertyType: 'text',
      sortable: true,
      colDefOverrides: {
        flex: 1,
        minWidth: 120,
        cellClassRules: getRowErrorClassRules(),
        ...(enableRowGroup && { valueFormatter: groupRowDash })
      }
    },
    {
      propertyName: 'errors',
      headerLabel: 'Error description',
      propertyType: 'text',
      sortable: false,
      formatForDisplay: (value: ContributionRecordError[]) => formatErrorDescriptions(value),
      colDefOverrides: {
        flex: 3,
        minWidth: 200,
        wrapText: true,
        autoHeight: true,
        cellClassRules: {
          'error-row-cell': (params: CellClassParams) => hasErrors(params),
          'error-text': (params: CellClassParams) => {
            if (params.node?.group) {
              return typeof params.value === 'number' && params.value > 0;
            }
            return hasErrors(params);
          }
        },
        cellStyle: ((params) =>
          params.node?.group ? { display: 'flex', alignItems: 'center' } : { whiteSpace: 'pre-wrap', lineHeight: '1.4' }) as CellStyleFunc,
        ...(enableRowGroup && {
          aggFunc: errorCountAggFunc,
          valueFormatter: (params: ValueFormatterParams) => {
            if (params.node?.group) {
              const count = params.value;
              if (typeof count === 'number' && count > 0) {
                return `${count} error${count === 1 ? '' : 's'}`;
              }
              return '';
            }
            const errors = params.value as ContributionRecordError[] | undefined;
            return formatErrorDescriptions(errors ?? []);
          }
        })
      }
    },
    {
      propertyName: 'contributionReasonDescription',
      headerLabel: 'Contributions reduction reason',
      propertyType: 'text',
      sortable: true,
      formatForDisplay: (value: string | null) => value ?? '',
      colDefOverrides: {
        flex: 2,
        minWidth: 180,
        cellClassRules: getRowErrorClassRules(),
        editable: false
      }
    },
    {
      propertyName: 'periodEarnings',
      headerLabel: 'Period earnings',
      propertyType: 'number',
      sortable: true,
      alignRight: true,
      formatForDisplay: (value: number) => columnFormatters.currency(value, 'GBP'),
      colDefOverrides: {
        flex: 1.5,
        minWidth: 130,
        type: 'numericColumn',
        cellClassRules: getRowErrorClassRules(),
        ...(enableRowGroup && { aggFunc: 'sum' })
      }
    },
    {
      propertyName: 'employeeContribution',
      headerLabel: 'Employee contribution',
      propertyType: 'number',
      sortable: true,
      alignRight: true,
      formatForDisplay: (value: number) => columnFormatters.currency(value, 'GBP'),
      colDefOverrides: {
        flex: 1.8,
        minWidth: 150,
        type: 'numericColumn',
        cellClassRules: getRowErrorClassRules(),
        ...(enableRowGroup && { aggFunc: 'sum' })
      }
    },
    {
      propertyName: 'employerContribution',
      headerLabel: 'Employer contribution',
      propertyType: 'number',
      sortable: true,
      alignRight: true,
      formatForDisplay: (value: number) => columnFormatters.currency(value, 'GBP'),
      colDefOverrides: {
        flex: 1.8,
        minWidth: 150,
        type: 'numericColumn',
        cellClassRules: getRowErrorClassRules(),
        ...(enableRowGroup && { aggFunc: 'sum' })
      }
    },
    {
      propertyName: 'total',
      headerLabel: 'Total',
      propertyType: 'number',
      sortable: true,
      alignRight: true,
      formatForDisplay: (value: number) => columnFormatters.currency(value, 'GBP'),
      textBold: () => true,
      colDefOverrides: {
        flex: 1.2,
        minWidth: 110,
        type: 'numericColumn',
        cellClassRules: getRowErrorClassRules(),
        ...(enableRowGroup && { aggFunc: 'sum' })
      }
    },
    {
      propertyName: 'pensionGroup',
      headerLabel: 'Pension group',
      propertyType: 'text',
      sortable: true,
      formatForDisplay: (value: string | null) => value || '-',
      colDefOverrides: {
        flex: 1.5,
        minWidth: 130,
        cellClassRules: getRowErrorClassRules()
      }
    }
  ];

  return showEmployerRefPaymentSourceColumn ? [employerRefPaymentSourceColumn, ...restColumns] : restColumns;
};
